import type { RowData } from "@tanstack/react-table";
import { TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/utils";
import { DataTableColumnHeader } from "./data-table-column-header";
import type { DataTableInstance } from "./use-data-table";
import { getDataTablePinningStyle } from "./use-data-table-column-pinning";

type DataTableHeaderProps<TData extends RowData> = {
  hasPinnedColumns: boolean;
  headerGroups: ReturnType<DataTableInstance<TData>["getHeaderGroups"]>[];
  table: DataTableInstance<TData>;
};

export function DataTableHeader<TData extends RowData>({ hasPinnedColumns, headerGroups, table }: DataTableHeaderProps<TData>) {
  return (
    <TableHeader className="sticky top-0 z-20 bg-muted">
      {headerGroups[0]?.map((headerGroup, depth) => (
        <TableRow className="border-0 bg-muted hover:bg-muted" key={headerGroup.id}>
          {headerGroups
            .flatMap((groups, index) =>
              (groups[depth]?.headers ?? []).map((header) => ({
                header,
                pinned: hasPinnedColumns ? ((["start", false, "end"] as const)[index] ?? false) : false,
              })),
            )
            .map(({ header, pinned }) => {
              const sorted = header.column.getIsSorted();
              const meta = header.column.columnDef.meta;
              const leaves = header.getLeafHeaders().filter((leaf) => leaf.subHeaders.length === 0);
              const edge = pinned === "end" ? leaves.at(-1) : leaves[0];
              const pinnedIndex = (edge?.column ?? header.column).getPinnedIndex();

              return (
                <TableHead
                  aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : undefined}
                  className={cn(
                    "relative h-9 bg-muted px-2 py-2 text-xs font-medium text-muted-foreground",
                    meta?.alignment === "center" && "text-center",
                    meta?.alignment === "right" && "text-end",
                    meta?.headerClassName,
                    pinned && "sticky z-10",
                  )}
                  colSpan={header.colSpan}
                  data-pinned={pinned || undefined}
                  data-pinned-index={pinned ? pinnedIndex : undefined}
                  key={header.id}
                  scope={header.colSpan > 1 ? "colgroup" : "col"}
                  style={getDataTablePinningStyle(pinned, pinnedIndex)}
                >
                  {header.isPlaceholder ? null : header.column.getCanSort() ? (
                    <DataTableColumnHeader column={header.column}>
                      <table.FlexRender header={header} />
                    </DataTableColumnHeader>
                  ) : (
                    <table.FlexRender header={header} />
                  )}
                </TableHead>
              );
            })}
        </TableRow>
      ))}
    </TableHeader>
  );
}
